
import React, { useState, useMemo, useEffect } from 'react';
import { 
  Plus, LayoutGrid, List as ListIcon, ChevronRight, ChevronLeft, Calendar as CalendarIcon, Settings, X, Filter, User as UserIcon, Trash2, Clock, AlertCircle, Table as TableIcon, CheckCircle2, Search, Trello, Paperclip, Download, CheckSquare, Square, ClipboardList, ArrowRight, Check, MousePointer2, BarChart3, Target, TrendingUp
} from 'lucide-react';
import SectionDashboard from './SectionDashboard';
import { TaskSector, TaskStatus, TaskPriority, Task, Contact, User, ChecklistItem, UserRole, Attendance, WhatsAppAssessor } from '../types';
import TaskDetailModal from './TaskDetailModal';
import AttendanceDetailModal from './AttendanceDetailModal';
import { supabase } from '../supabase';

const columns: { id: TaskStatus; color: string }[] = [
  { id: TaskStatus.TODO, color: 'bg-slate-400' },
  { id: TaskStatus.IN_PROGRESS, color: 'bg-parliament-600' },
  { id: TaskStatus.WAITING, color: 'bg-orange-400' },
  { id: TaskStatus.DONE, color: 'bg-emerald-500' },
];

interface KanbanBoardProps {
  tasks: Task[];
  attendances: Attendance[];
  currentUser: User;
  users: User[];
  onUpdateTask: (id: string, updates: Partial<Task>) => void;
  onDeleteTask: (id: string) => Promise<boolean>;
  onUpdateAttendance: (id: string, updates: Partial<Attendance>) => void;
  onDeleteAttendance: (id: string) => void;
  onOpenCreate: (initialStatus?: TaskStatus) => void;
  columnNames: Record<string, string>;
  onUpdateColumnName: (status: string, newName: string) => void;
  contacts: Contact[];
  sectors: TaskSector[];
  onAddSector: (sector: TaskSector) => void;
  onRemoveSector: (sector: TaskSector) => void;
  openTaskId?: string | null;
  onBulkDeleteTasks?: (ids: string[]) => Promise<void>;
  waAssessors?: WhatsAppAssessor[];
}

type ViewMode = 'kanban' | 'list' | 'calendar' | 'dashboard';
type DueFilter = 'all' | 'today' | 'week' | 'overdue';
type CalendarSource = 'tasks' | 'attendances' | 'both';

const KanbanBoard: React.FC<KanbanBoardProps> = ({ 
  tasks, 
  attendances,
  currentUser,
  users,
  onUpdateTask, 
  onDeleteTask,
  onUpdateAttendance,
  onDeleteAttendance,
  onOpenCreate, 
  contacts,
  sectors,
  openTaskId,
  onBulkDeleteTasks,
  waAssessors = []
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('kanban');
  const [dueFilter, setDueFilter] = useState<DueFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSector, setSelectedSector] = useState<TaskSector | 'all'>('all');
  const [selectedUser, setSelectedUser] = useState<string | 'all'>('all');
  const [selectedPriority, setSelectedPriority] = useState<TaskPriority | 'all'>('all');
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedAttendanceId, setSelectedAttendanceId] = useState<string | null>(null);
  const [listSortCol, setListSortCol] = useState<string>('');
  const [listSortDir, setListSortDir] = useState<'asc' | 'desc'>('asc');
  
  // Selection State for Bulk Actions
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  // Drag and Drop Visual States
  const [draggingTaskId, setDraggingTaskId] = useState<string | null>(null);
  const [dragOverColumnId, setDragOverColumnId] = useState<TaskStatus | null>(null);

  // Estados do Calendário em Grade
  const [calendarSource, setCalendarSource] = useState<CalendarSource>('both');
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    if (openTaskId) {
      setSelectedTaskId(openTaskId);
    }
  }, [openTaskId]);

  const selectedTask = useMemo(() => tasks.find(t => t.id === selectedTaskId) || null, [selectedTaskId, tasks]);
  const selectedAttendance = useMemo(() => attendances.find(a => a.id === selectedAttendanceId) || null, [selectedAttendanceId, attendances]);

  const assignableUsers = useMemo(() => {
    const list = [...users];
    if (!list.find(u => u.id === currentUser.id)) list.push(currentUser);
    return list.filter(u => u.role !== UserRole.LEADERSHIP);
  }, [users, currentUser]);

  const filteredTasks = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + (7 - today.getDay()));

    return tasks.filter(t => {
      const matchSearch = t.title.toLowerCase().includes(searchTerm.toLowerCase());
      const matchSector = selectedSector === 'all' || t.sector === selectedSector;
      const matchUser = selectedUser === 'all' || t.assignees?.includes(selectedUser) || t.stakeholders?.includes(selectedUser);
      const matchPriority = selectedPriority === 'all' || t.priority === selectedPriority;
      
      let matchDue = true;
      if (t.duedate) {
        const dueDate = new Date(t.duedate + 'T12:00:00');
        dueDate.setHours(0, 0, 0, 0);
        if (dueFilter === 'today') matchDue = dueDate.getTime() === today.getTime();
        else if (dueFilter === 'week') matchDue = dueDate >= today && dueDate <= endOfWeek;
        else if (dueFilter === 'overdue') matchDue = dueDate < today && t.status !== TaskStatus.DONE;
      } else if (dueFilter !== 'all') {
        matchDue = false;
      }
      return matchSearch && matchSector && matchUser && matchPriority && matchDue;
    });
  }, [tasks, searchTerm, selectedSector, selectedUser, selectedPriority, dueFilter]);

  const sortedListTasks = useMemo(() => {
    if (!listSortCol) return filteredTasks;
    const sorted = [...filteredTasks].sort((a, b) => {
      let va: any, vb: any;
      if (listSortCol === 'title') { va = a.title.toLowerCase(); vb = b.title.toLowerCase(); }
      else if (listSortCol === 'status') { va = a.status; vb = b.status; }
      else if (listSortCol === 'priority') { const ord: Record<string, number> = { 'Alta': 3, 'Média': 2, 'Baixa': 1 }; va = ord[a.priority] || 0; vb = ord[b.priority] || 0; }
      else if (listSortCol === 'duedate') { va = a.duedate || '9999'; vb = b.duedate || '9999'; }
      else return 0;
      if (va < vb) return listSortDir === 'asc' ? -1 : 1;
      if (va > vb) return listSortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredTasks, listSortCol, listSortDir]);

  const toggleListSort = (col: string) => {
    if (listSortCol === col) setListSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setListSortCol(col); setListSortDir('asc'); }
  };

  // Bulk Actions Logic
  const toggleSelectTask = (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const next = new Set(selectedTaskIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedTaskIds(next);
  };

  const handleTaskClick = (id: string) => {
    if (isSelectionMode) {
      toggleSelectTask(id);
    } else {
      setSelectedTaskId(id);
    }
  };

  const toggleSelectAll = () => {
    if (selectedTaskIds.size === filteredTasks.length && filteredTasks.length > 0) {
      setSelectedTaskIds(new Set());
    } else {
      setSelectedTaskIds(new Set(filteredTasks.map(t => t.id)));
    }
  };

  const toggleSelectionMode = () => {
    if (isSelectionMode) {
      // Sair do modo
      setIsSelectionMode(false);
      setSelectedTaskIds(new Set());
    } else {
      // Entrar no modo
      setIsSelectionMode(true);
    }
  };

  const handleBulkDelete = async () => {
    if (!onBulkDeleteTasks) return;
    await onBulkDeleteTasks(Array.from(selectedTaskIds));
    setSelectedTaskIds(new Set());
    setShowBulkDeleteConfirm(false);
    setIsSelectionMode(false);
  };

  const handleBulkExport = () => {
    const tasksToExport = selectedTaskIds.size > 0 
      ? tasks.filter(t => selectedTaskIds.has(t.id))
      : filteredTasks;

    const headers = ['Título', 'Status', 'Prioridade', 'Setor', 'Prazo', 'Responsável'];
    const csvContent = [
      headers.join(','),
      ...tasksToExport.map(t => {
        const assigneeName = users.find(u => u.id === t.assignees?.[0])?.name || 'Não atribuído';
        return [
          `"${t.title.replace(/"/g, '""')}"`,
          `"${t.status}"`,
          `"${t.priority}"`,
          `"${t.sector}"`,
          `"${t.duedate ? new Date(t.duedate + 'T12:00:00').toLocaleDateString('pt-BR') : ''}"`,
          `"${assigneeName}"`
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `tarefas_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setIsSelectionMode(false);
    setSelectedTaskIds(new Set());
  };

  // Lógica de Calendário em Grade
  const calendarDays = useMemo(() => {
    const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    
    // Ajuste para começar no Domingo (0)
    const firstDayIndex = startOfMonth.getDay();
    const days = [];

    // Dias do mês anterior para preencher a primeira semana
    const prevMonthLastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 0).getDate();
    for (let i = firstDayIndex; i > 0; i--) {
      days.push({
        date: new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, prevMonthLastDay - i + 1),
        currentMonth: false
      });
    }

    // Dias do mês atual
    for (let i = 1; i <= endOfMonth.getDate(); i++) {
      days.push({
        date: new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i),
        currentMonth: true
      });
    }

    // Dias do próximo mês para completar a grade de 6 semanas (42 dias)
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, i),
        currentMonth: false
      });
    }

    return days;
  }, [currentMonth]);

  const itemsByDate = useMemo(() => {
    const map: Record<string, any[]> = {};
    
    // Adicionar Tarefas Filtradas
    if (calendarSource === 'tasks' || calendarSource === 'both') {
      filteredTasks.forEach(t => {
        if (!t.duedate) return;
        const d = t.duedate.split('T')[0];
        if (!map[d]) map[d] = [];
        map[d].push({ ...t, itemType: 'task' });
      });
    }

    // Adicionar Atendimentos
    if (calendarSource === 'attendances' || calendarSource === 'both') {
      attendances.forEach(a => {
        const d = a.created_at.split('T')[0];
        if (!map[d]) map[d] = [];
        map[d].push({ ...a, title: a.demand, itemType: 'attendance' });
      });
    }

    return map;
  }, [filteredTasks, attendances, calendarSource]);

  const handleDragStart = (e: React.DragEvent, taskId: string) => { 
    if (!currentUser.permissions.canEditTasks || isSelectionMode) return;
    setDraggingTaskId(taskId);
    e.dataTransfer.setData('taskId', taskId); 
    // Efeito fantasma suave
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggingTaskId(null);
    setDragOverColumnId(null);
  };
  
  const handleDrop = async (e: React.DragEvent, newStatus: TaskStatus) => {
    if (!currentUser.permissions.canEditTasks || isSelectionMode) return;
    e.preventDefault();
    setDraggingTaskId(null);
    setDragOverColumnId(null);

    const taskId = e.dataTransfer.getData('taskId');
    const task = tasks.find(t => t.id === taskId);
    if (task && task.status !== newStatus) {
      onUpdateTask(taskId, { 
        status: newStatus,
        history: [...(task.history || []), {
          id: `h-${Date.now()}`,
          userId: currentUser.id,
          timestamp: new Date().toISOString(),
          action: `Alterou status para ${newStatus}`
        }]
      });
    }
  };

  const changeMonth = (offset: number) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + offset, 1));
  };

  const priorityStyles = {
    [TaskPriority.LOW]: 'text-slate-400 bg-slate-50 border-slate-100',
    [TaskPriority.MEDIUM]: 'text-parliament-600 bg-parliament-50 border-parliament-100',
    [TaskPriority.HIGH]: 'text-orange-500 bg-orange-50 border-orange-100',
    [TaskPriority.URGENT]: 'text-red-500 bg-red-50 border-red-100',
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 relative">
      {/* BULK DELETE CONFIRMATION */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 z-[150] bg-navy-950/80 backdrop-blur-sm flex flex-col items-center justify-center p-8 text-center animate-in fade-in zoom-in-95">
           <div className="w-full max-w-md bg-white dark:bg-navy-900 p-8 rounded-[32px] border border-neutral-200 dark:border-navy-800 shadow-2xl" onClick={e => e.stopPropagation()}>
             <div className="w-20 h-20 bg-red-500/20 rounded-[24px] flex items-center justify-center mb-6 shadow-xl mx-auto text-red-500"><Trash2 size={32} /></div>
             <h3 className="text-3xl font-black text-navy-950 dark:text-white tracking-tighter mb-2">Excluir {selectedTaskIds.size} Tarefas?</h3>
             <p className="text-slate-500 dark:text-slate-400 font-medium mb-8">Esta ação moverá as tarefas selecionadas para a lixeira.</p>
             <div className="flex gap-4 justify-center">
                <button onClick={() => setShowBulkDeleteConfirm(false)} className="px-8 py-3 bg-neutral-100 dark:bg-navy-800 text-slate-600 dark:text-slate-300 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-neutral-200 transition-all">Cancelar</button>
                <button onClick={handleBulkDelete} className="px-8 py-3 bg-red-600 text-white rounded-xl font-black text-[10px] uppercase shadow-xl hover:scale-105 transition-all">Confirmar</button>
             </div>
           </div>
        </div>
      )}

      {/* HEADER E FILTROS */}
      <div className="flex flex-col gap-6 px-2">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-4xl font-black tracking-tighter text-navy-950 dark:text-white leading-none">Fluxo de Trabalho</h2>
            <div className="flex items-center gap-2 mt-4 bg-neutral-100 dark:bg-navy-900 p-1 rounded-xl w-fit">
              <button onClick={() => setViewMode('kanban')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[10px] font-black uppercase transition-all ${viewMode === 'kanban' ? 'bg-white dark:bg-navy-800 text-black dark:text-white shadow-sm' : 'text-slate-400'}`}><LayoutGrid size={14} /> Kanban</button>
              <button onClick={() => setViewMode('list')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[10px] font-black uppercase transition-all ${viewMode === 'list' ? 'bg-white dark:bg-navy-800 text-black dark:text-white shadow-sm' : 'text-slate-400'}`}><ListIcon size={14} /> Lista</button>
              <button onClick={() => setViewMode('calendar')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[10px] font-black uppercase transition-all ${viewMode === 'calendar' ? 'bg-white dark:bg-navy-800 text-black dark:text-white shadow-sm' : 'text-slate-400'}`}><CalendarIcon size={14} /> Calendário</button>
              <button onClick={() => setViewMode('dashboard')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[10px] font-black uppercase transition-all ${viewMode === 'dashboard' ? 'bg-white dark:bg-navy-800 text-black dark:text-white shadow-sm' : 'text-slate-400'}`}><BarChart3 size={14} /> Dashboard</button>
            </div>
          </div>
          <div className="flex items-center gap-3">
             {/* BOTÃO MODO SELEÇÃO */}
             <button 
                onClick={toggleSelectionMode} 
                className={`px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition-all shadow-sm ${isSelectionMode ? 'bg-parliament-100 dark:bg-parliament-900/30 text-parliament-600 border border-parliament-200' : 'bg-white dark:bg-navy-900 text-slate-500 border border-neutral-100 dark:border-navy-800 hover:bg-neutral-50'}`}
             >
                {isSelectionMode ? <CheckSquare size={18}/> : <MousePointer2 size={18}/>} 
                {isSelectionMode ? 'Modo Seleção Ativo' : 'Selecionar'}
             </button>

             {/* AÇÕES EM LOTE (Aparecem no modo seleção ou se houver selecionados) */}
             {(isSelectionMode || selectedTaskIds.size > 0) && (
                <div className="flex items-center gap-2 animate-in slide-in-from-right-2">
                   {selectedTaskIds.size > 0 && (
                     <>
                        <button 
                          onClick={handleBulkExport} 
                          className="p-4 bg-white dark:bg-navy-900 text-slate-500 dark:text-slate-400 rounded-2xl hover:bg-neutral-50 dark:hover:bg-navy-800 transition-all shadow-sm border border-neutral-200 dark:border-navy-800 flex gap-2 font-bold text-xs items-center" 
                          title="Exportar Selecionados"
                        >
                            <Download size={20} />
                            Exportar ({selectedTaskIds.size})
                        </button>
                        {onBulkDeleteTasks && (
                          <button 
                            onClick={() => setShowBulkDeleteConfirm(true)} 
                            className="p-4 bg-red-50 dark:bg-red-900/20 text-red-600 rounded-2xl hover:bg-red-100 dark:hover:bg-red-900/40 transition-all shadow-sm border border-red-100 dark:border-red-900/30 flex gap-2 font-bold text-xs items-center" 
                            title="Excluir Selecionados"
                          >
                              <Trash2 size={20} />
                              Excluir ({selectedTaskIds.size})
                          </button>
                        )}
                     </>
                   )}
                   <button 
                      onClick={toggleSelectAll} 
                      className="p-4 bg-white dark:bg-navy-900 text-slate-500 rounded-2xl border border-neutral-200 dark:border-navy-800 font-bold text-xs"
                      title="Selecionar Todos Visíveis"
                   >
                      {selectedTaskIds.size === filteredTasks.length ? 'Limpar' : 'Todos'}
                   </button>
                   <button 
                      onClick={() => { setIsSelectionMode(false); setSelectedTaskIds(new Set()); }} 
                      className="p-4 bg-neutral-100 dark:bg-navy-800 text-slate-500 rounded-2xl font-bold text-xs"
                   >
                      <X size={20} />
                   </button>
                </div>
             )}

             {!isSelectionMode && (
                <button onClick={() => onOpenCreate()} className="bg-navy-950 dark:bg-parliament-600 text-white px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-3 shadow-xl hover:scale-[1.02] active:scale-95 transition-all">
                   <Plus size={18} /> Nova Tarefa
                </button>
             )}
          </div>
        </div>

        {/* BARRA DE PESQUISA E FILTROS */}
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-4">
          <div className="relative group lg:col-span-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-parliament-600" size={16} />
            <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Pesquisar..." className="w-full pl-10 pr-4 py-2.5 bg-neutral-50 dark:bg-navy-900 border border-neutral-100 dark:border-navy-800 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-parliament-600 dark:text-white" />
          </div>
          
          {viewMode !== 'calendar' ? (
            <>
              <div className="flex items-center gap-2 bg-neutral-50 dark:bg-navy-900 rounded-xl px-3 py-2 border border-neutral-100 dark:border-navy-800">
                <Filter size={12} className="text-slate-400" />
                <select value={selectedSector} onChange={(e) => setSelectedSector(e.target.value)} className="bg-transparent border-none text-[9px] font-black uppercase tracking-widest outline-none dark:text-white w-full">
                  <option value="all">TODOS OS SETORES</option>
                  {sectors.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2 bg-neutral-50 dark:bg-navy-900 rounded-xl px-3 py-2 border border-neutral-100 dark:border-navy-800">
                <UserIcon size={12} className="text-slate-400" />
                <select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)} className="bg-transparent border-none text-[9px] font-black uppercase tracking-widest outline-none dark:text-white w-full">
                  <option value="all">TODOS OS ASSESSORES</option>
                  {assignableUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  {waAssessors.length > 0 && <option disabled>──────────</option>}
                  {waAssessors.map(a => <option key={`wa_${a.id}`} value={`wa_${a.id}`}>{a.name}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2 bg-neutral-50 dark:bg-navy-900 rounded-xl px-3 py-2 border border-neutral-100 dark:border-navy-800">
                <CalendarIcon size={12} className="text-slate-400" />
                <select value={dueFilter} onChange={(e) => setDueFilter(e.target.value as DueFilter)} className="bg-transparent border-none text-[9px] font-black uppercase tracking-widest outline-none dark:text-white w-full">
                  <option value="all">QUALQUER PRAZO</option>
                  <option value="today">VENCE HOJE</option>
                  <option value="week">VENCE ESTA SEMANA</option>
                  <option value="overdue">EM ATRASO</option>
                </select>
              </div>
            </>
          ) : (
            <div className="lg:col-span-4 flex items-center justify-between">
                <div className="flex items-center gap-2 bg-neutral-50 dark:bg-navy-900 rounded-xl px-4 py-1.5 border border-neutral-100 dark:border-navy-800 w-fit">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest mr-2">Exibir:</span>
                  <button onClick={() => setCalendarSource('tasks')} className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all ${calendarSource === 'tasks' ? 'bg-navy-950 text-white shadow-md' : 'text-slate-400 hover:bg-white'}`}>Tarefas</button>
                  <button onClick={() => setCalendarSource('attendances')} className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all ${calendarSource === 'attendances' ? 'bg-navy-950 text-white shadow-md' : 'text-slate-400 hover:bg-white'}`}>Atendimentos</button>
                  <button onClick={() => setCalendarSource('both')} className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all ${calendarSource === 'both' ? 'bg-navy-950 text-white shadow-md' : 'text-slate-400 hover:bg-white'}`}>Ambos</button>
                </div>
                
                <div className="flex items-center gap-4 bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 px-4 py-1.5 rounded-xl shadow-sm">
                   <button onClick={() => changeMonth(-1)} className="p-1 hover:bg-neutral-100 dark:hover:bg-navy-800 rounded-lg text-slate-400"><ChevronLeft size={18}/></button>
                   <span className="text-[11px] font-black uppercase tracking-widest text-navy-950 dark:text-white min-w-[120px] text-center">
                     {currentMonth.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                   </span>
                   <button onClick={() => changeMonth(1)} className="p-1 hover:bg-neutral-100 dark:hover:bg-navy-800 rounded-lg text-slate-400"><ChevronRight size={18}/></button>
                </div>
            </div>
          )}
        </div>
      </div>

      {/* RENDERIZAÇÃO DOS MODOS */}
      {viewMode === 'kanban' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 min-h-[650px] px-2">
          {columns.map(col => (
            <div 
              key={col.id} 
              onDragOver={(e) => { e.preventDefault(); }} 
              onDragEnter={() => { if(draggingTaskId) setDragOverColumnId(col.id); }}
              onDrop={(e) => handleDrop(e, col.id)} 
              className={`rounded-[32px] p-5 flex flex-col border-2 transition-colors duration-200 ${dragOverColumnId === col.id ? 'bg-slate-100 dark:bg-navy-800 border-parliament-400 ring-2 ring-parliament-400/20' : 'bg-neutral-50 dark:bg-navy-900 border-transparent dark:border-navy-800'}`}
            >
              <div className="flex items-center justify-between mb-6 px-1">
                <div className="flex items-center gap-2.5">
                   <div className={`w-2.5 h-2.5 rounded-full ${col.color}`}></div>
                   <h3 className="font-black text-navy-900 dark:text-white text-[10px] uppercase tracking-[0.2em]">{col.id}</h3>
                   <span className="bg-neutral-200 dark:bg-navy-800 text-[8px] px-1.5 py-0.5 rounded-full font-black text-slate-500 dark:text-slate-400">{filteredTasks.filter(t => t.status === col.id).length}</span>
                </div>
              </div>
              <div className="space-y-4 flex-1 overflow-y-auto custom-scrollbar pr-1 pb-4">
                {filteredTasks.filter(t => t.status === col.id).map(task => (
                    <div 
                      key={task.id} 
                      draggable={currentUser.permissions.canEditTasks && !isSelectionMode} 
                      onDragStart={(e) => handleDragStart(e, task.id)} 
                      onDragEnd={handleDragEnd}
                      onClick={() => handleTaskClick(task.id)} 
                      className={`relative bg-white dark:bg-navy-800 p-5 rounded-2xl border shadow-sm hover:shadow-xl transition-all cursor-pointer group ${isSelectionMode && selectedTaskIds.has(task.id) ? 'border-parliament-500 ring-2 ring-parliament-500/20' : 'border-neutral-200 dark:border-navy-700'} ${draggingTaskId === task.id ? 'opacity-40 scale-95 grayscale' : 'opacity-100 scale-100'}`}
                    >
                      {/* Checkbox em Modo Seleção */}
                      {isSelectionMode && (
                        <div className="absolute top-4 right-4 z-10">
                           {selectedTaskIds.has(task.id) 
                              ? <CheckSquare size={20} className="text-parliament-600 fill-parliament-50" /> 
                              : <Square size={20} className="text-slate-300" />
                           }
                        </div>
                      )}

                      <div className="flex justify-between items-start mb-3 pr-6">
                        <span className={`text-[8px] font-black px-2.5 py-1 rounded-lg uppercase tracking-widest border ${priorityStyles[task.priority]}`}>{task.priority}</span>
                      </div>
                      <h4 className="font-bold text-navy-950 dark:text-white text-sm leading-tight mb-2 group-hover:text-parliament-600 transition-colors">{task.title}</h4>
                      {task.duedate && (
                         <div className="flex items-center gap-1.5 text-[9px] font-bold text-slate-400 mt-3">
                            <Clock size={10} /> {new Date(task.duedate + 'T12:00:00').toLocaleDateString('pt-BR')}
                         </div>
                      )}
                    </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {viewMode === 'list' && (
        <div className="bg-white dark:bg-navy-900 rounded-[32px] border border-neutral-200 dark:border-navy-800 overflow-hidden mx-2 transition-all animate-in slide-in-from-bottom-4">
           <table className="w-full text-left">
              <thead>
                 <tr className="bg-neutral-50 dark:bg-navy-950 text-slate-400 text-[10px] font-black uppercase tracking-widest border-b border-neutral-100">
                    {/* Coluna Checkbox sempre visível na lista para facilitar */}
                    <th className="px-4 py-5 w-10 text-center">
                       <button onClick={toggleSelectAll} className="text-slate-400 hover:text-navy-900 dark:hover:text-white">
                          {selectedTaskIds.size > 0 && selectedTaskIds.size === filteredTasks.length ? <CheckSquare size={18} /> : <Square size={18} />}
                       </button>
                    </th>
                    <th className="px-8 py-5 cursor-pointer hover:text-navy-900 dark:hover:text-white select-none" onClick={() => toggleListSort('title')}>Tarefa {listSortCol === 'title' ? (listSortDir === 'asc' ? '↑' : '↓') : ''}</th>
                    <th className="px-8 py-5 cursor-pointer hover:text-navy-900 dark:hover:text-white select-none" onClick={() => toggleListSort('status')}>Status {listSortCol === 'status' ? (listSortDir === 'asc' ? '↑' : '↓') : ''}</th>
                    <th className="px-8 py-5 cursor-pointer hover:text-navy-900 dark:hover:text-white select-none" onClick={() => toggleListSort('priority')}>Prioridade {listSortCol === 'priority' ? (listSortDir === 'asc' ? '↑' : '↓') : ''}</th>
                    <th className="px-8 py-5 cursor-pointer hover:text-navy-900 dark:hover:text-white select-none" onClick={() => toggleListSort('duedate')}>Prazo {listSortCol === 'duedate' ? (listSortDir === 'asc' ? '↑' : '↓') : ''}</th>
                    <th className="px-8 py-5 text-right">Ação</th>
                 </tr>
              </thead>
              <tbody className="divide-y divide-neutral-50 dark:divide-navy-800">
                 {sortedListTasks.map(t => (
                   <tr key={t.id} onClick={() => handleTaskClick(t.id)} className={`hover:bg-neutral-50 dark:hover:bg-navy-950/50 cursor-pointer group transition-colors ${selectedTaskIds.has(t.id) ? 'bg-neutral-50 dark:bg-navy-950/30' : ''}`}>
                      <td className="px-4 py-6 text-center" onClick={(e) => e.stopPropagation()}>
                         <button onClick={(e) => toggleSelectTask(t.id, e)} className="text-slate-300 hover:text-parliament-600">
                            {selectedTaskIds.has(t.id) ? <CheckSquare size={18} className="text-parliament-600" /> : <Square size={18} />}
                         </button>
                      </td>
                      <td className="px-8 py-6">
                         <div className="flex items-center gap-4">
                            <div className="p-2 rounded-xl bg-navy-900 text-white"><Trello size={16}/></div>
                            <p className="font-bold text-sm text-navy-950 dark:text-white group-hover:text-parliament-600">{t.title}</p>
                         </div>
                      </td>
                      <td className="px-8 py-6"><span className="px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border border-neutral-100 dark:border-navy-700 dark:text-slate-400">{t.status}</span></td>
                      <td className="px-8 py-6"><span className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase border ${priorityStyles[t.priority]}`}>{t.priority}</span></td>
                      <td className="px-8 py-6 text-xs font-bold text-slate-600 dark:text-slate-400">{t.duedate ? new Date(t.duedate + 'T12:00:00').toLocaleDateString('pt-BR') : '-'}</td>
                      <td className="px-8 py-6 text-right"><ChevronRight size={20} className="text-slate-300 group-hover:text-parliament-600 transition-all"/></td>
                   </tr>
                 ))}
              </tbody>
           </table>
        </div>
      )}

      {viewMode === 'calendar' && (
        <div className="bg-white dark:bg-navy-950 rounded-[40px] border border-neutral-200 dark:border-navy-800 overflow-hidden mx-2 shadow-sm animate-in fade-in">
           {/* Cabeçalho dos Dias da Semana */}
           <div className="grid grid-cols-7 border-b border-neutral-100 dark:border-navy-800 bg-neutral-50 dark:bg-navy-900/50">
              {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(d => (
                <div key={d} className="py-4 text-center text-[10px] font-black text-slate-400 uppercase tracking-widest">{d}</div>
              ))}
           </div>
           
           {/* Grade de Dias */}
           <div className="grid grid-cols-7 border-collapse">
              {calendarDays.map((day, idx) => {
                const dateKey = day.date.toISOString().split('T')[0];
                const dayItems = itemsByDate[dateKey] || [];
                const isToday = new Date().toDateString() === day.date.toDateString();

                return (
                  <div key={idx} className={`min-h-[140px] p-2 border-r border-b border-neutral-100 dark:border-navy-800 transition-colors ${day.currentMonth ? 'bg-white dark:bg-navy-900' : 'bg-neutral-50/50 dark:bg-navy-950 opacity-40'}`}>
                     <div className="flex justify-between items-center mb-2">
                        <span className={`text-[10px] font-black w-6 h-6 flex items-center justify-center rounded-lg ${isToday ? 'bg-parliament-600 text-white shadow-lg' : 'text-slate-400'}`}>
                           {day.date.getDate()}
                        </span>
                     </div>
                     
                     <div className="space-y-1.5">
                        {dayItems.slice(0, 3).map(item => (
                           <div 
                              key={item.id}
                              onClick={() => item.itemType === 'task' ? handleTaskClick(item.id) : setSelectedAttendanceId(item.id)}
                              className={`px-2 py-1.5 rounded-lg border text-[9px] font-bold truncate cursor-pointer transition-all hover:scale-[1.02] shadow-sm ${item.itemType === 'task' ? 'bg-navy-50 dark:bg-navy-800 border-navy-100 dark:border-navy-700 text-navy-950 dark:text-white' : 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400'}`}
                              title={item.title}
                           >
                              <div className="flex items-center gap-1.5">
                                 {item.itemType === 'task' && isSelectionMode && selectedTaskIds.has(item.id) && <CheckSquare size={10} className="text-parliament-600"/>}
                                 {item.itemType === 'task' ? <Trello size={10}/> : <ClipboardList size={10}/>}
                                 <span className="truncate">{item.title}</span>
                              </div>
                           </div>
                        ))}
                        {dayItems.length > 3 && (
                           <div className="text-[8px] font-black text-slate-400 uppercase tracking-widest text-center py-1">
                              + {dayItems.length - 3} itens
                           </div>
                        )}
                     </div>
                  </div>
                );
              })}
           </div>
        </div>
      )}

      {viewMode === 'dashboard' && (() => {
        const today = new Date(); today.setHours(0,0,0,0);
        const total = filteredTasks.length;
        const done = filteredTasks.filter(t => t.status === 'Concluído').length;
        const active = filteredTasks.filter(t => t.status !== 'Concluído' && t.status !== 'Arquivado').length;
        const overdue = filteredTasks.filter(t => t.duedate && new Date(t.duedate + 'T12:00:00') < today && t.status !== 'Concluído' && t.status !== 'Arquivado').length;

        const pieData = [
          { name: 'A Fazer', value: filteredTasks.filter(t => t.status === 'A Fazer').length, color: '#f59e0b' },
          { name: 'Em andamento', value: filteredTasks.filter(t => t.status === 'Em andamento').length, color: '#3b82f6' },
          { name: 'Aguardando', value: filteredTasks.filter(t => t.status === 'Aguardando').length, color: '#8b5cf6' },
          { name: 'Concluído', value: done, color: '#10b981' },
        ];

        const userMap: Record<string, number> = {};
        filteredTasks.forEach(t => { (t.assignees || []).forEach(uid => { const u = users.find(x => x.id === uid); if (u) userMap[u.name] = (userMap[u.name] || 0) + 1; }); });
        const barData = Object.entries(userMap).map(([name, value]) => ({ name: name.split(' ')[0], value })).sort((a, b) => b.value - a.value);

        const monthMap: Record<string, number> = {};
        filteredTasks.forEach(t => { const d = t.history?.[0]?.timestamp || t.duedate; if (d) { const m = d.substring(0, 7); monthMap[m] = (monthMap[m] || 0) + 1; } });
        const areaData = Object.entries(monthMap).sort(([a],[b]) => a.localeCompare(b)).slice(-6).map(([m, v]) => ({ name: m.substring(5) + '/' + m.substring(2, 4), value: v }));

        return (
          <SectionDashboard
            stats={[
              { label: 'Total', value: total, color: 'text-slate-500', icon: Trello },
              { label: 'Concluídas', value: done, color: 'text-emerald-500', icon: CheckCircle2 },
              { label: 'Ativas', value: active, color: 'text-blue-500', icon: TrendingUp },
              { label: 'Atrasadas', value: overdue, color: 'text-red-500', icon: AlertCircle },
            ]}
            pieData={pieData}
            pieTitle="Por Status"
            barData={barData}
            barTitle="Por Responsável"
            barColor="#0f172a"
            areaData={areaData}
            areaTitle="Evolução Mensal"
          />
        );
      })()}

      {/* MODAIS DE DETALHES */}
      {selectedTask && (
        <TaskDetailModal 
          task={selectedTask} currentUser={currentUser} users={users} waAssessors={waAssessors} onClose={() => setSelectedTaskId(null)}
          onUpdateTask={onUpdateTask} onDeleteTask={onDeleteTask} contacts={contacts} sectors={sectors}
        />
      )}
      {selectedAttendance && (
        <AttendanceDetailModal 
          attendance={selectedAttendance} users={users} contacts={contacts} onClose={() => setSelectedAttendanceId(null)}
          onUpdateAttendance={onUpdateAttendance} onDeleteAttendance={async (id) => { onDeleteAttendance(id); return true; }} currentUser={currentUser}
        />
      )}
    </div>
  );
};

export default KanbanBoard;
